import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { Head, Link, usePage } from '@inertiajs/react';
import { router } from '@inertiajs/react';
import { route } from '../../utils/route';
import { assetUrl } from '../../utils/assetUrl';

const b2cValueProps = [
  {
    icon: 'fa-truck',
    title: 'Delivery a domicilio',
    description: 'Recibí tus productos frescos en la puerta de tu casa'
  },
  {
    icon: 'fa-leaf',
    title: 'Calidad garantizada',
    description: 'Seleccionamos los mejores productos del mercado'
  },
  {
    icon: 'fa-clock',
    title: 'Ahorrá tiempo',
    description: 'Pedí desde tu celular sin salir de casa'
  },
  {
    icon: 'fa-refresh',
    title: 'Política de reemplazo',
    description: 'Si algo no está bien, lo reponemos sin costo'
  }
];

const b2bValueProps = [
  {
    icon: 'fa-headphones',
    title: 'Atención personalizada',
    description: 'Un asesor dedicado para tu negocio'
  },
  {
    icon: 'fa-check-circle',
    title: 'Calidad consistente',
    description: 'Productos seleccionados para tu negocio'
  },
  {
    icon: 'fa-cubes',
    title: 'Stock confiable',
    description: 'Disponibilidad garantizada para tu operación'
  },
  {
    icon: 'fa-credit-card',
    title: 'Cuenta corriente',
    description: 'Financiación para clientes gastronómicos'
  }
];

interface HeroSectionProps {
  showValueProps?: boolean;
  showOnlyValueProps?: boolean;
  nextDeliveryLabel?: string | null;
}

export function HeroSection({ showValueProps = true, showOnlyValueProps = false, nextDeliveryLabel }: HeroSectionProps): React.ReactElement {
  const { cdnUrl, defaultCategorySlug } = usePage<{ cdnUrl: string; defaultCategorySlug: string }>().props;
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const heroDesktop = assetUrl(cdnUrl, '/img/hero-landing.webp?v=3');
  const heroMobile = assetUrl(cdnUrl, '/img/hero-landing-mobile.webp?v=4');

  useEffect(() => {
    if (window.innerWidth >= 640) {
      searchInputRef.current?.focus();
    }
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const baseUrl = route('products.index', { cat: defaultCategorySlug });
    if (searchQuery.trim()) {
      router.visit(`${baseUrl}?p=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      router.visit(baseUrl);
    }
  };

  return (
    <>
      {/* Preload hero background images with high priority for better LCP */}
      <Head>
        <link
          rel="preload"
          as="image"
          href={heroDesktop}
          fetchPriority="high"
          media="(min-width: 769px)"
        />
        <link
          rel="preload"
          as="image"
          href={heroMobile}
          fetchPriority="high"
          media="(max-width: 768px)"
        />
      </Head>
      <section className="hero-section">
      <style>{`
        .hero-section {
          position: relative;
          background: url(${heroDesktop}) center center/cover no-repeat;
          padding: 80px 0 90px;
        }

        .hero-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.65);
          pointer-events: none;
        }

        .hero-container {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .hero-title {
          text-align: center;
          font-size: 32px;
          font-weight: 700;
          color: #fff;
          margin-bottom: 10px;
          text-shadow: 0 2px 8px rgba(0,0,0,0.4);
        }

        .hero-subtitle {
          text-align: center;
          font-size: 18px;
          color: rgba(255,255,255,0.9);
          margin-bottom: 8px;
          text-shadow: 0 1px 4px rgba(0,0,0,0.3);
        }

        .hero-delivery-hint {
          text-align: center;
          font-size: 15px;
          font-weight: 600;
          color: #4ade80;
          margin-bottom: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        /* Search Bar */
        .hero-search {
          max-width: 600px;
          margin: 0 auto;
        }

        .hero-search-form {
          display: flex;
          gap: 10px;
        }

        .hero-search-input {
          flex: 1;
          padding: 14px 20px;
          font-size: 16px;
          border: 2px solid #ddd;
          border-radius: 8px;
          outline: none;
          transition: border-color 0.2s;
          -webkit-appearance: none;
        }

        .hero-search-input:focus {
          border-color: #5cb85c;
        }


        .hero-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 16px;
          padding: 14px 28px;
          font-size: 16px;
          font-weight: 600;
          color: #fff;
          background: #1EAA25;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s;
          text-decoration: none;
          width: 100%;
        }

        .hero-cta:hover {
          background: #178f1e;
          color: #fff;
          text-decoration: none;
        }

        /* Value Props Grid */
        .value-props-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
        }

        .value-props-column {
          background: #fff;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .value-props-header {
          font-size: 18px;
          font-weight: 600;
          color: #333;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 2px solid #5cb85c;
        }

        .value-prop-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
        }

        .value-prop-item:last-child {
          margin-bottom: 0;
        }

        .value-prop-icon {
          width: 36px;
          height: 36px;
          min-width: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e8f5e8;
          border-radius: 8px;
          color: #5cb85c;
          font-size: 16px;
        }

        .value-prop-content {
          flex: 1;
        }

        .value-prop-title {
          font-size: 15px;
          font-weight: 600;
          color: #333;
          margin-bottom: 2px;
        }

        .value-prop-description {
          font-size: 13px;
          color: #666;
          line-height: 1.4;
        }

        .b2b-links {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          margin-top: 16px;
        }

        .gastro-link-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          background: #1EAA25;
          border-radius: 8px;
          text-decoration: none;
          transition: background 0.2s;
        }
        .gastro-link-btn:hover {
          background: #178f1e;
          color: #fff;
          text-decoration: none;
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .hero-section {
            padding: 24px 0 32px;
            background-image: url(${heroMobile});
            background-position: center 20%;
          }

          .hero-title {
            font-size: 24px;
          }

          .hero-subtitle {
            font-size: 15px;
            margin-bottom: 6px;
          }

          .hero-delivery-hint {
            font-size: 13px;
            margin-bottom: 20px;
          }

          .hero-search-input {
            padding: 12px 16px;
          }

          .hero-cta {
            font-size: 16px;
            padding: 14px 24px;
          }

          .value-props-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .value-props-column {
            padding: 16px;
          }

          .value-props-header {
            font-size: 16px;
            margin-bottom: 16px;
          }

          .value-prop-item {
            margin-bottom: 12px;
          }

          .value-prop-icon {
            width: 32px;
            height: 32px;
            min-width: 32px;
            font-size: 14px;
          }

          .value-prop-title {
            font-size: 14px;
          }

          .value-prop-description {
            font-size: 12px;
          }
        }
      `}</style>

      <div className="hero-container">
        {!showOnlyValueProps && (
          <>
            <h1 className="hero-title">El mercado que va a vos</h1>
            <p className="hero-subtitle">
              Frutas y verduras frescas con delivery en Córdoba
            </p>
            {nextDeliveryLabel && (
              <p className="hero-delivery-hint">
                <i className="fa fa-truck"></i>
                Recibí tu pedido {nextDeliveryLabel}
              </p>
            )}

            {/* Search Bar */}
            <div className="hero-search">
              <form className="hero-search-form" onSubmit={handleSearch}>
                <input
                  ref={searchInputRef}
                  type="search"
                  inputMode="search"
                  enterKeyHint="search"
                  className="hero-search-input"
                  placeholder="Buscá frutas, verduras y más..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </form>
              <Link href={route('products.index', { cat: defaultCategorySlug })} className="hero-cta">
                Ver todos los productos
                <i className="fa fa-arrow-right"></i>
              </Link>
            </div>
          </>
        )}

        {/* Value Propositions Grid */}
        {(showValueProps || showOnlyValueProps) && (
        <div className="value-props-grid">
          {/* B2C Column */}
          <div className="value-props-column">
            <h2 className="value-props-header">
              <i className="fa fa-home" style={{ marginRight: 8 }}></i>
              Para tu hogar
            </h2>
            {b2cValueProps.map((prop, idx) => (
              <div key={idx} className="value-prop-item">
                <div className="value-prop-icon">
                  <i className={`fa ${prop.icon}`}></i>
                </div>
                <div className="value-prop-content">
                  <div className="value-prop-title">{prop.title}</div>
                  <div className="value-prop-description">{prop.description}</div>
                </div>
              </div>
            ))}
            <div className="b2b-links">
              <Link href="/por-que-elegirnos" className="gastro-link-btn">
                Por qué elegirnos <i className="fa fa-arrow-right" style={{ fontSize: 12 }}></i>
              </Link>
            </div>
          </div>

          {/* B2B Column */}
          <div className="value-props-column">
            <h2 className="value-props-header">
              <i className="fa fa-building" style={{ marginRight: 8 }}></i>
              Para tu negocio
            </h2>
            {b2bValueProps.map((prop, idx) => (
              <div key={idx} className="value-prop-item">
                <div className="value-prop-icon">
                  <i className={`fa ${prop.icon}`}></i>
                </div>
                <div className="value-prop-content">
                  <div className="value-prop-title">{prop.title}</div>
                  <div className="value-prop-description">{prop.description}</div>
                </div>
              </div>
            ))}
            <div className="b2b-links">
              <Link href="/gastronomicos" className="gastro-link-btn">
                Servicio gastronómico <i className="fa fa-arrow-right" style={{ fontSize: 12 }}></i>
              </Link>
              <Link href="/verdulerias" className="gastro-link-btn">
                Servicio para verdulerías <i className="fa fa-arrow-right" style={{ fontSize: 12 }}></i>
              </Link>
            </div>
          </div>
        </div>
        )}
      </div>
      </section>
    </>
  );
}
